import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { fighterModelUrl, fighters, arenaLandmarkUrl, arenas, worldActorUrl } from './content'
import { SCENARIO_ASSET_URLS, scenarioProfileForArena } from './generatedArenaScenarios'
import { WORLD_COMPOSITIONS, WORLD_LIBRARY_URLS } from './generatedWorldExpansion'
import { WORLD_FORGE_COMPOSITIONS } from './generatedWorldForge'
import { arenaCompositionPlan } from './arenaCompositionAuthority'
import { useFightStore } from './store'
import { isAssetWarm, warmCachedAsset } from './assetWarmup'
import { runtimePublicPath } from './runtimePublicPath'

type BootPhase = 'roster' | 'warm' | 'world' | 'blocked' | 'ready'
type BootState = {
  phase: BootPhase
  queueKey: string
  completed: number
  total: number
  failedCritical: string[]
  failedOptional: string[]
}

const CONCURRENCY = 6
const WARM_CONCURRENCY = 1
const FETCH_TIMEOUT_MS = 15000
const WARM_TIMEOUT_MS = 45000
const VERIFIED_ASSETS=new Set<string>()
const STAGE_ASSET_URLS=[
  '/polyhaven/models/wooden_crate_01.glb','/polyhaven/models/old_military_crate.glb','/polyhaven/models/Barrel_01.glb',
  '/polyhaven/models/boulder_01.glb','/polyhaven/models/concrete_road_barrier.glb','/polyhaven/models/gothic_statue.glb',
  '/polyhaven/models/metal_jerrycan.glb','/polyhaven/models/metal_toolbox.glb','/polyhaven/models/crystalline_iceplant.glb',
  '/polyhaven/models/moon_rock_01.glb','/polyhaven/models/bark_debris_01.glb','/polyhaven/models/stone_01.glb',
]

function unique(values: string[]) {
  return [...new Set(values.filter(Boolean))]
}

function arenaHotUrls(arenaIndex:number){
  const arena=arenas[((arenaIndex%arenas.length)+arenas.length)%arenas.length]
  if(!arena)return[]
  const plan=arenaCompositionPlan(arena.id),profile=scenarioProfileForArena(arena.id)
  const expansion=WORLD_COMPOSITIONS[arena.id]
  const forge=WORLD_FORGE_COMPOSITIONS[arena.id as keyof typeof WORLD_FORGE_COMPOSITIONS]
  const scenarioAssets=SCENARIO_ASSET_URLS as Record<string,string>
  const worldAssets=WORLD_LIBRARY_URLS as Record<string,string>
  const expansionUrls=expansion?expansion.pieces.map(piece=>worldAssets[piece.asset]).filter(Boolean):[]
  const forgeUrls=forge?(Object.values(forge.layers).flat() as Array<{asset:string}>).map(piece=>scenarioAssets[piece.asset]).filter(Boolean):[]
  const dressingUrls=plan.dressingGeometry?profile.dressing.map(item=>scenarioAssets[item.asset]).filter(Boolean):[]
  return unique([
    ...(plan.landmark?[arenaLandmarkUrl(arena)]:[]),
    ...arena.worldActors.map(worldActorUrl),
    ...expansionUrls.map(runtimePublicPath),...forgeUrls.map(runtimePublicPath),...dressingUrls.map(runtimePublicPath),...STAGE_ASSET_URLS.map(runtimePublicPath),
    `${runtimePublicPath(profile.pbr.base)}/environment.hdr`,
    `${runtimePublicPath(profile.pbr.base)}/ground_diff.jpg`,
    `${runtimePublicPath(profile.pbr.base)}/ground_nor.jpg`,
    `${runtimePublicPath(profile.pbr.base)}/ground_rough.jpg`,
  ])
}

function buildBootQueue(playerIndex:number,enemyIndex:number,arenaIndex:number) {
  const selectedFighters=unique([
    fighters[playerIndex]?fighterModelUrl(fighters[playerIndex]):'',
    fighters[enemyIndex]?fighterModelUrl(fighters[enemyIndex]):'',
  ])
  // Preserve canonical HEAD-only fighter verification: renderer owns the GLB GET/decode.
  const deferredWorld=arenaHotUrls(arenaIndex).filter(url=>!selectedFighters.includes(url))
  // Warm only four arena PBR inputs after entry, not the entire universe or selected rig GLBs.
  const hot=deferredWorld.filter(url=>/\/(environment\.hdr|ground_(diff|nor|rough)\.jpg)$/i.test(url)).slice(0,4)
  return {
    critical:selectedFighters,
    hot,
    optional:[] as string[],
    deferredWorld,
  }
}

async function probeAsset(url: string) {
  if(VERIFIED_ASSETS.has(url))return
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
  try {
    const response = await fetch(url, { method: 'HEAD', cache: 'no-store', signal: controller.signal })
    if (!response.ok) throw new Error(`${response.status} ${response.statusText}`)
    VERIFIED_ASSETS.add(url)
  } finally {
    window.clearTimeout(timer)
  }
}

async function warmAsset(url:string){
  if(isAssetWarm(url))return
  await warmCachedAsset(url,WARM_TIMEOUT_MS)
  VERIFIED_ASSETS.add(url)
}

async function runPool(
  urls: string[],
  onProgress: (url: string, ok: boolean) => void,
  cancelled: () => boolean,
  loader:(url:string)=>Promise<void>=probeAsset,
  concurrency=CONCURRENCY,
) {
  let cursor = 0
  const failed: string[] = []
  const workers = Array.from({ length: Math.min(concurrency, Math.max(1, urls.length)) }, async () => {
    while (!cancelled()) {
      const index = cursor++
      if (index >= urls.length) return
      const url = urls[index]
      try {
        await loader(url)
        if (!cancelled()) onProgress(url, true)
      } catch {
        failed.push(url)
        if (!cancelled()) onProgress(url, false)
      }
    }
  })
  await Promise.all(workers)
  return failed
}

export function FightBootGate({ children }: { children: ReactNode }) {
  const playerIndex=useFightStore(s=>s.playerIndex),enemyIndex=useFightStore(s=>s.enemyIndex),arenaIndex=useFightStore(s=>s.arenaIndex),status=useFightStore(s=>s.status)
  const queue = useMemo(()=>buildBootQueue(playerIndex,enemyIndex,arenaIndex),[playerIndex,enemyIndex,arenaIndex])
  const queueKey=`${playerIndex}:${enemyIndex}:${arenaIndex}`
  // The visible entry meter tracks blocking selected-rig HEADs only.
  const totalOperations=queue.critical.length
  const [state, setState] = useState<BootState>({
    phase: 'roster',
    queueKey,
    completed: 0,
    total: totalOperations,
    failedCritical: [],
    failedOptional: [],
  })
  const [attempt, setAttempt] = useState(0)
  const runtimeEntered = useRef(false)

  useEffect(() => {
    let cancelled = false
    let completed = 0
    let warmTimer:number|undefined
    const advance = (kind: 'critical' | 'optional') => (url: string, ok: boolean) => {
      if(kind==='critical')completed += 1
      setState((current) => ({
        ...current,
        completed,
        failedCritical: kind === 'critical' && !ok
          ? [...current.failedCritical, url]
          : current.failedCritical,
        failedOptional: kind === 'optional' && !ok
          ? [...current.failedOptional, url]
          : current.failedOptional,
      }))
    }

    setState({
      phase: 'roster',
      queueKey,
      completed: 0,
      total: totalOperations,
      failedCritical: [],
      failedOptional: [],
    })

    ;(async () => {
      const criticalFailures = await runPool(queue.critical, advance('critical'), () => cancelled)
      if (cancelled) return
      if (criticalFailures.length > 0) {
        setState((current) => ({ ...current, phase: 'blocked' }))
        return
      }
      // Selected fighters are the only blocking runtime dependency. The richer arena/world
      // catalog remains valuable, but warms in background so it can never stall entry.
      setState((current) => ({ ...current, phase: 'ready' }))
      // Bounded background prewarm: never prevent entry, never issue a second fighter GLB GET.
      warmTimer=window.setTimeout(()=>{
        if(cancelled)return
        void runPool(queue.hot,advance('optional'),()=>cancelled,warmAsset,WARM_CONCURRENCY)
          .then(failed=>failed.forEach(url=>console.warn('[RiftFighters] optional PBR warm failed:',url)))
      },750)
      if(queue.optional.length)void runPool(queue.optional,advance('optional'),()=>cancelled)
    })()

    return () => {
      cancelled = true
      if(warmTimer!==undefined)window.clearTimeout(warmTimer)
    }
  }, [queue, queueKey, totalOperations, attempt])

  // After match completion, verify the remaining world catalog quietly at one HEAD at a time.
  useEffect(()=>{
    if(state.phase!=='ready'||state.queueKey!==queueKey||status!=='match-over'||queue.deferredWorld.length===0)return
    let cancelled=false
    const timer=window.setTimeout(()=>{
      void runPool(queue.deferredWorld,(url,ok)=>{if(!ok)console.warn('[RiftFighters] deferred world integrity probe failed:',url)},()=>cancelled,probeAsset,1)
    },1200)
    return()=>{cancelled=true;window.clearTimeout(timer)}
  },[state.phase,state.queueKey,queue,queueKey,status])

  if (state.phase === 'ready'&&state.queueKey===queueKey) runtimeEntered.current=true
  if (runtimeEntered.current&&state.phase!=='blocked') return <>{children}</>

  const progress = state.total > 0 ? Math.round((state.completed / state.total) * 100) : 100
  return (
    <div className="fight-boot" data-testid="fight-boot">
      <div className="fight-boot__scan" />
      <div className="fight-boot__core">
        <span className="fight-boot__eyebrow">INYRON // RIFT FIGHTERS</span>
        <h1>INITIALIZING COMBAT WORLD</h1>
        <p>
          {state.failedCritical.length||state.failedOptional.length
            ? 'A required combat-world asset failed integrity loading. Entry remains blocked until the asset graph is complete.'
            : state.phase === 'roster'
              ? 'Verifying the complete fighter roster and runtime identities.'
              : state.phase === 'warm'
                ? 'Preloading the selected fighters, arena geometry, PBR surfaces and stage props into browser cache.'
                : 'Verifying every remaining world dependency before rendering the combat scene.'}
        </p>
        <div className="fight-boot__meter" aria-label={`Loading ${progress}%`}>
          <div style={{ width: `${progress}%` }} />
        </div>
        <div className="fight-boot__readout">
          <strong>{String(progress).padStart(3, '0')}%</strong>
          <span>{state.completed}/{state.total} ASSETS</span>
        </div>
        <div className="fight-boot__stages">
          <span className={state.phase === 'blocked' ? '' : 'active'}>01 ROSTER + RIGS</span>
          <span>02 WORLD + PBR</span>
          <span>03 GPU READY</span>
        </div>
        {(state.failedCritical.length > 0||state.failedOptional.length>0) && (
          <div className="fight-boot__critical">
            <small>{state.failedCritical.length+state.failedOptional.length} required asset(s) failed. No degraded scene will be rendered.</small>
            <button type="button" onClick={() => setAttempt((value) => value + 1)}>RETRY REQUIRED ASSETS</button>
          </div>
        )}
      </div>
    </div>
  )
}
